import { cssStyleLoader, isFeatureEnabled } from "@picovico/utils";
import useWebConfig, { getComponentToRender } from "hooks/useWebConfig";
import { useEffect, useMemo } from "react";
import { useAppDispatch, useAppSelector } from "store/hooks";
import { loadConfigAsync } from "store/slices/configSlice";
import { Box, Text } from "@chakra-ui/react";
import { FullPageLoader } from "@components/index";
import NotFoundView from "@components/NotFoundView";
import SiteChrome from "@components/SiteChrome";
import { NextPageWithLayoutAndPageType } from "types/base";
import { withoutDisabledFeatureLinks } from "utils/featureLinks";
import BannerPopUpWrapper from "./BannerPopUpWrapper";

function withSiteConfig(NextPage: NextPageWithLayoutAndPageType) {
  const getPageLayout = NextPage.getLayout || ((page) => page);
  const page_type = NextPage.page_type || null;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return function WithSiteConfig(props: any) {
    const { configInitialized, configLoadFail, ...config } = useAppSelector(
      (state) => state.config,
    );
    const { pageConfig, headerElements, footerElements } = useWebConfig({
      config,
      page_type: page_type,
    });
    const user = useAppSelector((state) => state.user);
    const dispatch = useAppDispatch();

    useEffect(() => {
      if (config.css_override) {
        cssStyleLoader(config.css_override);
      }
    }, [config]);

    useEffect(() => {
      if (!configInitialized) {
        // if (props.config) {
        //   dispatch(updateConfig(props.config));
        //   storeLocalStorageData("companyIdx", props.config.company_idx);
        //   storeLocalStorageData("tDom", props.config.tenant_domain);
        // } else {
        dispatch(loadConfigAsync());
        // }
      }
    }, [dispatch, configInitialized, props.config]);

    const componentElements = useMemo(() => {
      const index = NextPage.pageComponentPlacementIndex;
      const bodyComponents = getComponentToRender(
        pageConfig?.components || [],
        "body_components",
        withoutDisabledFeatureLinks(
          config.features,
          config.template_props?.body_components_props || {},
        ),
      );
      const page = getPageLayout(<NextPage key={page_type} {...props} />);
      let renderComponents = bodyComponents;
      if (!index || bodyComponents?.length - 1 > index) {
        renderComponents.push(page);
      } else {
        renderComponents = bodyComponents
          .slice(0, index)
          .concat([page])
          .concat(bodyComponents.slice(index));
      }
      return renderComponents;
    }, [
      pageConfig,
      props,
      config.template_props?.body_components_props,
      config.features,
    ]);

    if (!page_type) {
      return getPageLayout(<NextPage {...props} />);
    }

    if (!configInitialized) {
      return <FullPageLoader />;
    }

    // A page that exists only for a feature the company has switched off is
    // not there: the same not-found view as an unconfigured page. Fail-open:
    // no `features` in the config means every feature is on.
    const featureOff =
      Boolean(NextPage.feature) &&
      !isFeatureEnabled(config.features, NextPage.feature);

    if (
      configLoadFail ||
      featureOff ||
      (configInitialized && !pageConfig && page_type)
    ) {
      if (!featureOff) {
        console.error(
          "error from with siteconfig",
          configLoadFail,
          configInitialized,
          pageConfig,
        );
      }
      // Render the tenant-customized 404 (with nav chrome) instead of Next's
      // bare default error page, so in-app navigation to an unconfigured page
      // matches the experience of a hard refresh onto /404.
      return (
        <SiteChrome>
          <NotFoundView />
        </SiteChrome>
      );
    }

    return (
      <BannerPopUpWrapper>
        <Box
          sx={{
            backgroundImage: config.layout_config?.background_image
              ? `${config.layout_config?.linear_gradient ? `${config.layout_config.linear_gradient} ,` : ""}url(${config.layout_config.background_image})`
              : config.layout_config?.background,
            background: config.layout_config?.background,
            backgroundRepeat: "no-repeat",
            backgroundSize: "cover",
            backgroundPosition: "center",
            // Fill the viewport so short pages don't expose the body color
            // (visible white band under dark-themed tenants).
            minHeight: "100dvh",
            imageRendering: "initial",
          }}
        >
          {headerElements}
          {!user?.user?.is_email_verified && user.isLoggedIn && (
            <Box p={2} bgColor={"warning.500"} fontWeight={"bold"}>
              <Text textAlign={"center"}>
                Your email is unverified. Please check your email for password
                to verify your email.
              </Text>
            </Box>
          )}
          <Box
            as="main"
            className="app-page-main"
            sx={{
              minHeight: "70vh",
              position: "relative",
            }}
            // Reserve space for the fixed mobile bottom nav so content isn't hidden.
            pb={
              config.layout_config?.mobile_config?.nav_style === "bottom_nav"
                ? { base: "calc(4rem + env(safe-area-inset-bottom))", xl: 0 }
                : undefined
            }
          >
            {componentElements}
          </Box>
          {config.layout_config?.mobile_config?.nav_style === "bottom_nav" ? (
            // Footer moves into the bottom-nav "More" sheet on mobile.
            <Box display={{ base: "none", xl: "block" }}>{footerElements}</Box>
          ) : (
            footerElements
          )}
        </Box>
      </BannerPopUpWrapper>
    );
  };
}

export default withSiteConfig;
